/**
 * Bundle-unlock state for the PDP cross-sell card (main-product block
 * `ux_suggested_item`).
 *
 * The card advertises the shop's automatic bundle discount. That discount is a
 * cart rule, not a property of the add-on, so the copy and the price have to
 * follow the cart:
 *
 *   - it fires on eligible units. A unit is eligible unless its line carries a
 *     selling plan, or is already discounted by its own product-scoped deal —
 *     stack products hold theirs from the moment they enter the cart, and the
 *     bundle discount does not count them (all verified against the live cart:
 *     one-time + one-time unlocks and discounts both lines; a subscription line
 *     never does; a stack line never does);
 *   - the ladder is 2 units -> 10%, 3 or more -> 15% (verified live, it caps
 *     there), so the percent the card shows depends on the unit count the cart
 *     reaches;
 *   - the add-on itself is always added one-time, so on the subscribe path it
 *     lands next to an ineligible line and unlocks nothing.
 *
 * States, in priority order:
 *
 *   unlocked  this product already carries a line-level discount in the cart →
 *             show the cart's own discounted price and its percent
 *   unlocks   one more eligible unit reaches a tier → show the price that tier
 *             leaves the cart charging right after this add
 *   plain     neither applies → list price, and no discount claim at all
 *
 * Liquid renders the `plain` state (the truth for a fresh, subscribe-first
 * cart); this script upgrades it once /cart.js confirms, and downgrades it again
 * whenever the cart changes. Multi-variant add-ons add through Dawn's quick-add
 * modal, so the card always advertises the variant it renders.
 *
 * Markup contract (see sections/main-product.liquid, block `ux_suggested_item`):
 *   [data-usi]                     block root + config attributes, including
 *                                  `data-usi-base-price` (cents) for the rendered variant
 *   [data-usi-label]               subtitle node owned by this script
 *   [data-usi-price|compare|compare-wrap] price nodes this script rewrites
 */
(function () {
  var ROOT_SELECTOR = '[data-usi]';
  var REFRESH_DEBOUNCE = 150;

  function ready(cb) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', cb);
    } else {
      cb();
    }
  }

  function money(cents, format) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, format || '${{amount}}');
    }
    var amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return String(format || '{{amount}}').replace(/\{\{\s*amount\s*\}\}/g, amount);
  }

  function moneyAmount(value, el) {
    var next = value == null ? '' : String(value).trim();
    if (!el || !Object.prototype.hasOwnProperty.call(el.dataset, 'usiMoneyAmount')) return next;

    var currency = String(el.dataset.usiCurrencyCode || '').trim();
    if (!currency) return next;

    var escaped = currency.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    next = next.replace(new RegExp('^\\s*' + escaped + '\\s*', 'i'), '');
    next = next.replace(new RegExp('\\s*' + escaped + '\\s*$', 'i'), '');
    return next.trim();
  }

  // Drops the FX anchors so assets/currency-converter.js re-converts the
  // swapped price, and the currency code the shop money format may prepend.
  function setText(el, value) {
    if (!el) return;
    var next = moneyAmount(value, el);
    if (el.textContent.trim() === next) return;
    delete el.dataset.fxAed;
    delete el.dataset.fxAedOut;
    el.textContent = next;
  }

  function percentLabel(percent) {
    var value = Math.round(percent * 10) / 10;
    return value % 1 === 0 ? String(value) : value.toFixed(1);
  }

  /**
   * A unit counts towards the bundle ladder unless its line is a subscription
   * (never eligible) or already carries a product-scoped deal of its own: stack
   * products hold theirs from the moment they enter the cart and the bundle
   * discount does not count them (verified live — a cart holding only The
   * Performance Stack gets nothing when a second product is added, while the
   * same add next to a plain product is discounted at the 2-unit tier).
   *
   * A discount counts as product-scoped when its title is the line's own product
   * title, which is how the shop names them ("The Performance Stack"). A cart
   * that proves the tier outright — a line already carrying the bundle discount
   * — is read from its allocation instead, so this heuristic only ever feeds the
   * pre-add prediction, and the cart corrects it as soon as the add lands.
   */
  function countsTowardsBundle(item) {
    if (item.selling_plan_allocation) return false;
    var productTitle = String(item.product_title || '').trim().toLowerCase();
    if (!productTitle) return true;
    return !(item.line_level_discount_allocations || []).some(function (allocation) {
      var application = (allocation && allocation.discount_application) || {};
      return String(application.title || '').trim().toLowerCase() === productTitle;
    });
  }

  function eligibleUnits(cart) {
    return (cart.items || []).reduce(function (total, item) {
      return countsTowardsBundle(item) ? total + (Number(item.quantity) || 0) : total;
    }, 0);
  }

  function allocationPercent(line) {
    var allocations = line.line_level_discount_allocations || [];
    for (var i = 0; i < allocations.length; i++) {
      var application = allocations[i].discount_application || {};
      if (application.value_type !== 'percentage') continue;
      var value = parseFloat(application.value);
      if (value > 0) return value;
    }
    return 0;
  }

  function linePercent(line) {
    var price = Number(line.price);
    var discounted = Number(line.discounted_price);
    if (!price || !discounted || discounted >= price) return 0;
    return ((price - discounted) * 100) / price;
  }

  /**
   * Percent the shop's automatic bundle discount applies at this eligible unit
   * count. Verified live: 2 units -> 10%, 3 or more -> 15% (it caps there).
   * A tier only counts once the cart reaches it, and the widest reached tier
   * wins, matching how Shopify settles the cart.
   */
  function bundleTierPercent(units, tiers) {
    var percent = 0;
    tiers.forEach(function (tier) {
      if (units >= tier.units && tier.percent > percent) percent = tier.percent;
    });
    return percent;
  }

  function resolveState(cart, productId, tiers) {
    var lines = (cart.items || []).filter(function (item) {
      return String(item.product_id) === String(productId);
    });

    for (var i = 0; i < lines.length; i++) {
      if (!(lines[i].line_level_discount_allocations || []).length) continue;
      var percent = allocationPercent(lines[i]) || linePercent(lines[i]);
      if (!(percent > 0)) continue;
      return { mode: 'unlocked', percent: percent, ratio: 1 - percent / 100 };
    }

    var unlockedPercent = bundleTierPercent(eligibleUnits(cart) + 1, tiers);
    if (unlockedPercent > 0) {
      return { mode: 'unlocks', percent: unlockedPercent, ratio: 1 - unlockedPercent / 100 };
    }

    return { mode: 'plain', percent: 0, ratio: 1 };
  }

  function stateLabel(root, state) {
    if (state.mode === 'plain') return root.getAttribute('data-usi-fallback') || '';
    var percent = percentLabel(state.percent);
    return state.mode === 'unlocked' ? percent + '% off unlocked' : 'Unlock ' + percent + '% off';
  }

  /** Reads a numeric block attribute; blank or unparsable falls back. */
  function numberAttribute(root, name, fallback) {
    var raw = root.getAttribute(name);
    if (raw == null || raw.trim() === '') return fallback;
    var value = parseFloat(raw);
    return isNaN(value) ? fallback : value;
  }

  function syncPriceNodes(root, state, base) {
    var format = root.getAttribute('data-usi-money-format') || '{{amount}}';
    var price = Math.round(base * state.ratio);
    var discounted = price < base;

    setText(root.querySelector('[data-usi-price]'), money(price, format));
    setText(root.querySelector('[data-usi-compare]'), discounted ? money(base, format) : '');
    var wrap = root.querySelector('[data-usi-compare-wrap]');
    if (wrap) wrap.hidden = !discounted;
  }

  function applyState(root, cart) {
    var productId = root.getAttribute('data-usi-product-id');
    if (!productId) return;

    var base = Number(root.getAttribute('data-usi-base-price'));
    if (!base) return;

    var tiers = [
      { units: 2, percent: numberAttribute(root, 'data-usi-unlock-percent', 10) },
      { units: 3, percent: numberAttribute(root, 'data-usi-unlock-percent-3', 15) },
    ];

    var state = resolveState(cart, productId, tiers);
    var label = root.querySelector('[data-usi-label]');
    if (label) {
      var text = stateLabel(root, state);
      label.hidden = !text;
      setText(label, text);
    }

    var signature = state.mode + ':' + state.percent;
    if (root.dataset.usiState === signature) return;
    root.dataset.usiState = signature;

    syncPriceNodes(root, state, base);
  }

  var cart = null;
  var timer = null;

  function refresh() {
    fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        cart = data;
        document.querySelectorAll(ROOT_SELECTOR).forEach(function (root) {
          applyState(root, cart);
        });
      })
      .catch(function () {
        /* Leave the server-rendered plain state in place: no cart, no claim. */
      });
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(refresh, REFRESH_DEBOUNCE);
  }

  ready(function () {
    // Dawn's quick-add modal re-injects this script with the product page it
    // fetched; the card is hidden there (quick-add-hidden), so skip its cart
    // read and pubsub subscription in that copy.
    if (document.currentScript && document.currentScript.closest('quick-add-modal')) return;
    if (!document.querySelector(ROOT_SELECTOR)) return;
    refresh();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, schedule);
    }
  });
})();
